import _ from "lodash";
import React from "react";
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";
import classNames from "classnames";
import classes from "./SmallLibraryItem.scss";
import DropDown from "./DropDown";
import { CommentIcon } from "SvgComponents";

import I18nHOC from "UIComponents/I18nHOC";
import ResourceImagePlaceHolder from "UIComponents/ResourceImagePlaceHolder";
import { colors } from "Constants";
import { getCurrentAssessmentTypes } from "Platform/modules/PlatformModule";
import { onEnterOrSpaceKeyDown } from "Utils";
import { updateHighlightTaskId } from "ClassRoom/modules/ClassRoomModule";
import { getUnitFlowUtils } from "UnitFlowV3/modules/utils";
import { getIcon } from "UIComponents/NestedAccordion/utils";
import { getSubtitle } from "Utils/EntityFlowUtils";
import { Tooltip } from "@toddle-edu/ds-web";

class LibraryItem extends React.PureComponent {
  componentDidMount() {
    const { highlightTaskId, item, updateHighlightTaskId } = this.props;

    // If highlightTaskId matches the current card's id, start the timer
    if (highlightTaskId === item.id) {
      setTimeout(() => {
        updateHighlightTaskId(null); // Remove highlight after 2 seconds
      }, 2000);
    }
  }

  render() {
    const {
      onCardClicked,
      item,
      onDeleteClick,
      onDuplicateClick,
      onPrintClick,
      readOnly,
      onAssignToStudentClick,
      t,
      curriculumType,
      provided,
      snapshot,
      innerRef,
      updateTagAssessmentId,
      totalNewSnpCycles,
      isTermsExists,
      getCurrentAssessmentTypes,
      onChangeTaskTypeClick,
      highlightTaskId,
      isUnitFlowRevamp,
      testId,
    } = this.props;

    const assessmentType = _.get(item, "assessmentType.value", "le") || "le";

    const currentAssessmentTypes = getCurrentAssessmentTypes();

    const assessmentTypeLabel = _.get(item, "isUbdAssessment", false)
      ? _.get(item, "assessmentType.label", "")
      : currentAssessmentTypes[assessmentType]?.localeLabel
      ? t(currentAssessmentTypes[assessmentType]?.localeLabel)
      : "";

    const updatedProps = {
      ...provided.draggableProps,
      ...provided.dragHandleProps,
    };

    const draggingStyle = snapshot.isDragging
      ? {
          boxShadow: "0 2px 8px 0 rgba(0, 0, 0, 0,.1)",
          border: `solid 1px ${colors.blue400}`,
        }
      : {};

    const style = {
      ...provided.draggableProps.style,
      ...draggingStyle,
    };

    const cardClasses = classNames(classes.item, {
      [classes.highlightAnimation]: highlightTaskId === item.id,
      [classes.itemRevamp]: isUnitFlowRevamp,
    });

    const itemContentContainerClasses = classNames(
      classes.itemContentContainer,
      {
        [classes.itemContentContainerRevamp]: isUnitFlowRevamp,
      }
    );

    const titleClasses = classNames({
      [classes.title]: !isUnitFlowRevamp,
      [classes.titleRevamped]: isUnitFlowRevamp,
      "text-body": isUnitFlowRevamp,
    });

    if (isUnitFlowRevamp) {
      classes.imageContainer = classNames(
        classes.imageContainer,
        classes.imageContainerRevamp
      );
      classes.noShrink = classNames(classes.noShrink, classes.noShrinkRevamp);
    }

    const subtitleClasses = classNames({
      [classes.subtitle]: !isUnitFlowRevamp,
      [classes.subtitleRevamped]: isUnitFlowRevamp,
      ["text-body-s"]: isUnitFlowRevamp,
    });

    return (
      <div
        ref={ref => innerRef(ref)}
        className={cardClasses}
        {...updatedProps}
        style={style}
        onClick={e => {
          if (!e.defaultPrevented) onCardClicked(item);
        }}
        onKeyDown={e => {
          onEnterOrSpaceKeyDown(e, () => onCardClicked(item));
        }}
        tabIndex={0}
        key={item.id}
        data-test-id={`${testId}-${item.id}-card`}
      >
        {isUnitFlowRevamp ? (
          getIcon({
            orgResource: {
              resourceType: "ASSESSMENT",
              item,
            },
            classes,
          })
        ) : (
          <div className={classes.itemImageContainer}>
            <ResourceImagePlaceHolder
              id={item.id}
              type={item.assessmentType}
              imageUrl={item.image.value}
            />
          </div>
        )}
        <div className={itemContentContainerClasses}>
          <div className={classes.itemDetailContainer}>
            <div className={classes.itemDetailLeftContainer}>
              <div className={classes.titleContainer}>
                <Tooltip
                  tooltip={
                    <div className={classes.titleTooltip}>
                      {!item?.title?.value
                        ? t("common:untitled")
                        : item.title.value}
                    </div>
                  }
                  placement="top"
                >
                  <div className={titleClasses}>
                    {!item?.title?.value
                      ? t("common:untitled")
                      : item.title.value}
                  </div>
                </Tooltip>
                <div className={classes.commentIcon}>
                  <CommentIcon width={24} height={24} fill={colors.strokeOne} />
                </div>
              </div>
              <div className={subtitleClasses}>
                {isUnitFlowRevamp
                  ? getSubtitle({
                      assessmentTypes: currentAssessmentTypes,
                      orgResource: {
                        resourceType: "ASSESSMENT",
                        item,
                      },
                    })
                  : assessmentTypeLabel}
              </div>
            </div>
          </div>
          {!readOnly && (
            <div
              className={classes.menuContainer}
              // Stop keydown event propogation to reach parent div
              onKeyDown={e => e.stopPropagation()}
              onPointerDown={e => e.stopPropagation()}
            >
              <DropDown
                item={item}
                curriculumType={curriculumType}
                onDeleteClick={onDeleteClick}
                onEditClick={onCardClicked}
                onDuplicateClick={onDuplicateClick}
                onPrintClick={onPrintClick}
                onAssignToStudentClick={onAssignToStudentClick}
                updateTagAssessmentId={updateTagAssessmentId}
                totalNewSnpCycles={totalNewSnpCycles}
                assessmentTypeLabel={assessmentTypeLabel}
                isTermsExists={isTermsExists}
                assessmentType={assessmentType}
                onChangeTaskTypeClick={onChangeTaskTypeClick}
                isUnitFlowRevamp={isUnitFlowRevamp}
                testId={`${testId}-${item.id}`}
              />
            </div>
          )}
        </div>
      </div>
    );
  }
}

const mapActionCreators = { getCurrentAssessmentTypes, updateHighlightTaskId };

const mapStateToProps = state => {
  const highlightTaskId = _.get(state, "classRoom.highlightTaskId", "");
  const { isUnitFlowRevampEnabled } = getUnitFlowUtils();
  const isUnitFlowRevamp = isUnitFlowRevampEnabled();

  return {
    highlightTaskId,
    isUnitFlowRevamp,
  };
};

export default compose(
  I18nHOC({ resource: "common" }),
  connect(mapStateToProps, mapActionCreators)
)(LibraryItem);
